import { UploadFile } from "antd";
import { AssetType } from "./AssetUploader";
import { AssetPurpose } from "../BrandKitAssets";
import { StagedBrandKitAsset } from "types/";

/** beforeUpload passes RcFile; fileList entries use originFileObj. */
export const getUploadFileBlob = (file: UploadFile): File | undefined => {
  const blob = file.originFileObj ?? file;
  return blob instanceof File || blob instanceof Blob
    ? (blob as File)
    : undefined;
};

/** PUTs a file straight to a presigned S3 URL, reporting real progress. */
export const putFileToS3 = (
  url: string,
  file: File,
  onProgress: (percent: number) => void
) =>
  new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    // NB: S3 requires this header to be empty, but that is subject to change since that is against the HTTP spec.
    xhr.setRequestHeader("Content-Type", "");

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error("Upload failed"));
      }
    };

    xhr.onerror = () => reject(new Error("Upload failed"));

    xhr.send(file);
  });

/** Replaces staged assets for this purpose/type with blob URLs for template preview. */
export const mergeStagedFilesForPurpose = (
  prev: StagedBrandKitAsset[],
  files: UploadFile[],
  purposeValue: AssetPurpose,
  assetType: AssetType
): StagedBrandKitAsset[] => {
  const matchesSlot = (s: StagedBrandKitAsset) => s.purpose === purposeValue;

  prev.filter(matchesSlot).forEach((s) => {
    if (s.path?.startsWith("blob:")) {
      URL.revokeObjectURL(s.path);
    }
  });

  const others = prev.filter((s) => !matchesSlot(s));

  const newEntries: StagedBrandKitAsset[] = files.flatMap((file) => {
    const blob = getUploadFileBlob(file);
    if (!blob) return [];
    return [
      {
        uid: file.uid,
        asset_id: file.uid,
        name: file.name,
        type: assetType,
        purpose: purposeValue,
        path: URL.createObjectURL(blob),
      },
    ];
  });

  return [...others, ...newEntries];
};
